import {
  Component,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ProductType } from 'src/app/core/models';
import {
  DefaultsService,
  AuthenticationService,
  EquipmentService,
  ProfileService,
} from 'src/app/core/providers';
import { NavService } from 'src/app/core/providers/services/nav.service';
import { SearchService } from 'src/app/core/providers/services/search.service';
import { ComponentBase } from '../component.base';
import { ProductTypeSortOption } from 'src/app/core/models/product-type-sort.model';
import { TranslateLabelPipe } from 'src/app/core/pipes/translate-label.pipe';
import {
  ProductTypeItemComponent,
  ProductTypeItemLabels,
} from './components/product-type-item.component';
import { FiltersService } from '../../filters/filters.service';
import { ColorSpectrumService } from './services/color-spectrum.service';

const DEBUG = false;

@Component({
  selector: 'app-product-types',
  templateUrl: './product-types.component.html',
  // styleUrls: ['./product-types.component.scss'],
  providers: [TranslateLabelPipe],
  standalone: false,
})
export class ProductTypesComponent
  extends ComponentBase
  implements OnInit, OnDestroy
{
  hasProductTypeSubscription = false;
  productTypes?: Array<ProductType> = [];
  public productTypeId?: number;
  categoryId: number;
  labels: ProductTypeItemLabels = {
    allProductsLabel: 'All Products',
  };
  showHourly = signal(false);
  showEstimates = signal(false);
  selectedProductSortType?: ProductTypeSortOption | null;
  initialSubscription: Subscription;
  localSubscriptions: Subscription[] = [];
  currentPage = signal(0);
  pageSize = 100;
  scrollDistance = 4;
  estimateColors: WritableSignal<string[]> = signal([]);

  constructor(
    public defaultsService: DefaultsService,
    public authenticationService: AuthenticationService,
    public equipmentService: EquipmentService,
    public profileService: ProfileService,
    public searchService: SearchService,
    public navService: NavService,
    public translateService: TranslateService,
    public route: ActivatedRoute,
    public router: Router,
    public filtersService: FiltersService,
    public colorService: ColorSpectrumService
  ) {
    super(
      defaultsService,
      authenticationService,
      equipmentService,
      profileService,
      searchService,
      navService,
      translateService,
      route,
      router,
      filtersService
    );
    this.initialSubscription = this.router.events.subscribe((e: any) => {
      // If it is a NavigationEnd event re-initalise the component
      if (e instanceof NavigationEnd) {
        if (this.categoryId) {
          this.searchResults = undefined;
          this.clearLocalSubscriptions();
          this.hasProductTypeSubscription = false;
          this.initialize();
        }
      }
    });
    this.estimateColors.set(this.colorService.getColors());
  }

  ngOnInit(): void {
    this.initialize();
  }

  initialize() {
    this.authenticationService.startSentryTransaction(
      this.router.routerState.snapshot.url
    );
    const categoryId = this.route.snapshot.paramMap.get('categoryId');
    this.selectedProductSortType =
      this.profileService.getSelectedProductSortType();
    this.labels.allProductsLabel = `${this.translateService.instant('main.data.all_assets')}`;
    if (this.router.url.toLocaleLowerCase().includes('search')) {
      this.equipmentService.productTypes = undefined;
    }
    if (!categoryId) {
      this.router.navigate(['/categories']);
    } else {
      this.localSubscriptions.push(
        this.profileService.selectedCustomersChanged.subscribe(() => {
          this.loadPageData();
        })
      );
      this.categoryId = +categoryId;
      // TODO: How do we get into this state? Where is productTypeId added to the query string?
      const productTypeId = this.route.snapshot.paramMap.get('productTypeId');
      this.productTypeId = productTypeId ? +productTypeId : undefined;
      const showHourlySetting = this.filterDefaults?.find(
        (r) => r.CodeName === 'mobile.rate.hourly.show'
      );
      this.showHourly.set(
        showHourlySetting && showHourlySetting.DefaultValue === 1 ? true : false
      );
      const showEstimateSetting = this.filterDefaults?.find(
        (r) => r.CodeName === 'mobile.rate.commission-estimate.show'
      );
      this.showEstimates.set(
        showEstimateSetting && showEstimateSetting.DefaultValue === 1
          ? true
          : false
      );
      if (DEBUG) {
        console.log(
          'product-types.component.initialize  showEstimates',
          this.filterDefaults,
          showEstimateSetting,
          this.showEstimates()
        );
      }
      this.populateRowLabels();
      this.loadPageData();
    }
  }

  ngOnDestroy() {
    this.clearAllSubscriptions();
  }

  clearAllSubscriptions() {
    this.clearLocalSubscriptions();
    this.subscriptions.forEach((s: Subscription) => {
      s.unsubscribe();
    });
    if (this.initialSubscription) {
      this.initialSubscription.unsubscribe();
    }
  }

  clearLocalSubscriptions() {
    this.localSubscriptions.forEach((s: Subscription) => {
      s.unsubscribe();
    });
  }

  getUtilFillColor = (productType: ProductType) => {
    let color = 'darkred';
    const current = productType.physical_utilization_current
      ? productType.physical_utilization_current
      : 0;
    const bench = productType.physical_utilization_bench
      ? productType.physical_utilization_bench
      : 0;
    if (current >= bench) {
      color = 'green';
    }
    return color;
  };

  populateRowLabels() {
    this.labels.row1Label = this.defaultLabels
      ? this.defaultLabels.find(
          (r) => r.CodeName === 'mobile.product-type.rate.first.row'
        )?.DisplayName
      : '';
    this.labels.row2Label = this.defaultLabels
      ? this.defaultLabels.find(
          (r) => r.CodeName === 'mobile.product-type.rate.second.row'
        )?.DisplayName
      : '';
    this.labels.row3Label = this.defaultLabels
      ? this.defaultLabels.find(
          (r) => r.CodeName === 'mobile.product-type.rate.third.row'
        )?.DisplayName
      : '';
    this.labels.row4Label = this.defaultLabels
      ? this.defaultLabels.find(
          (r) => r.CodeName === 'mobile.product-type.rate.fourth.row'
        )?.DisplayName
      : '';
    this.labels.row5Label = this.defaultLabels
      ? this.defaultLabels.find(
          (r) => r.CodeName === 'mobile.product-type.rate.fifth.row'
        )?.DisplayName
      : '';
    this.labels.customerRowLabel = this.defaultLabels
      ? this.defaultLabels.find(
          (r) => r.CodeName === 'mobile.product-type.rate.customer.row'
        )?.DisplayName
      : '';
    this.labels.rateTypeLabel = this.translateService.instant(
      'main.mobile.rate_type'
    );
  }

  onScrollDown() {
    this.loadNextPage();
  }

  protected loadNextPage() {
    this.currentPage.set(this.currentPage() + 1);
    this.equipmentService.getProductTypes(
      this.categoryId,
      this.currentPage(),
      this.pageSize,
      this.productTypeId
    );
  }

  loadPageData = () => {
    console.log('product-types.component.loadPageData  called');
    this.productTypes = [];
    this.isBusy = true;
    this.currentPage.set(0);
    if (!this.hasProductTypeSubscription) {
      this.localSubscriptions.push(
        this.equipmentService.productTypesLoaded.subscribe((productTypes) => {
          console.log(
            'product-types.component.productTypesLoaded subscription  called',
            productTypes
          );
          if (
            this.categoryId !== -999 &&
            productTypes &&
            productTypes.length > 1 &&
            productTypes[0].product_type_id === -999
          ) {
            if (
              !this.labels.allProductsLabel?.includes(
                productTypes[1].category_name
              )
            ) {
              this.labels.allProductsLabel = `${this.labels.allProductsLabel} (${this.navService.pageTitle})`;
            }
          }

          // productTypeId will be send from the search results
          if (!this.productTypeId) {
            this.productTypes = productTypes;
          } else {
            this.productTypes = productTypes.filter(
              (r) => r.product_type_id === this.productTypeId
            );
          }
          this.isBusy = false;
          this.authenticationService.finishSentryTransaction();
        })
      );
      this.hasProductTypeSubscription = true;
    }
    this.loadNextPage();
  };

  openAssetAvailability = (productType: ProductType) => {
    if (!productType.is_asset) {
      return;
    }
    this.equipmentService.assetAvailabilitySummary = undefined;

    this.navService.pageTitle =
      productType.product_type_id !== -999
        ? productType.product_type_name
        : this.labels.allProductsLabel;

    this.router.navigate([
      '/asset-availability',
      this.categoryId,
      productType.product_type_id,
    ]);
  };
}
